/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1777852800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon May 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adform",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Adform",
          "depth": 0,
          "id": "connect-to-adform"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing conversion events",
          "depth": 1,
          "id": "syncing-conversion-events"
        }, {
          "value": "Syncing audience segments",
          "depth": 1,
          "id": "syncing-audience-segments"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Conversion Event"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion event data to Adform"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.adformhelp.com/hc/en-us/articles/9740579489041-Use-Server-Side-Tracking",
                children: "Conversions docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment"
              })
            }), _jsx(_components.td, {
              children: "Sync users from your model to Adform DMP"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://api.adform.com/help/references/audience-base",
                children: "Audience base (DMP) docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adform",
        children: "Connect to Adform"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), ", select ", _jsx(_components.strong, {
          children: "Add destination"
        }), ", select ", _jsx(_components.strong, {
          children: "Adform"
        }), ", and then select ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Adform supports two sync types with different credentials. Enter the fields for the sync types you plan to use:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["For conversion syncs:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "Tracking Setup ID"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "Tracking Domain"
              })
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["For segment syncs:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "Client ID"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "Client Secret"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can enter one set of credentials or both if you plan to run both sync types from the same destination."
      }), "\n", _jsx(_components.p, {
        children: "For segment syncs, ask Adform Support to grant your OAuth client these scopes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.segments"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.categories"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.authenticated"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.dataproviders.readonly"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Adform destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), ", select ", _jsx(_components.strong, {
          children: "Add sync"
        }), ", and then select the relevant model and Adform destination."]
      }), "\n", _jsxs(_components.p, {
        children: ["For each sync, select ", _jsx(_components.strong, {
          children: "Conversion Event"
        }), " or ", _jsx(_components.strong, {
          children: "Segment"
        }), " for ", _jsx(_components.strong, {
          children: "Sync type"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsx(_components.p, {
        children: "Sync conversion event data to Adform."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to conversions in Adform on any column in your model and any field in Adform. Ensure the data types of the model column and Adform field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via field mapping."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audience-segments",
        children: "Syncing audience segments"
      }), "\n", _jsx(_components.p, {
        children: "Sync users from your model to Adform DMP segments.\nYou can sync to an existing segment or create a new one on the first sync."
      }), "\n", _jsx(_components.p, {
        children: "Adform segment syncs are add-only.\nIf records leave your model, Hightouch doesn't remove them from the segment."
      }), "\n", _jsx(_components.h4, {
        id: "select-a-data-provider",
        children: "Select a data provider"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the Adform data provider that owns the segments you want to sync.\nHightouch fetches the available data providers using the destination's ", _jsx(_components.strong, {
          children: "Client ID"
        }), " and ", _jsx(_components.strong, {
          children: "Client Secret"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-segment-or-create-a-new-one",
        children: "Select an existing segment or create a new one"
      }), "\n", _jsx(_components.p, {
        children: "You can sync to an existing segment or create a new segment in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "If you create a new segment, you also need to configure these fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Category"
          }), ": Every Adform segment must belong to one category. You can select an existing category or create a new one on the first sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data consumer ID"
          }), ": Optional. Enter an agency ID or publisher ID if another Adform entity should have access to the segment."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "TTL (days)"
          }), ": Optional. Controls how long a user remains in the segment after their last upload. The default is ", _jsx(_components.code, {
            children: "30"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Frequency"
          }), ": Optional. Controls the minimum number of occurrences required for a user to qualify for the segment. The default is ", _jsx(_components.code, {
            children: "1"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "select-a-user-identity-type",
        children: "Select a user identity type"
      }), "\n", _jsx(_components.p, {
        children: "Select the Adform identity type for the user IDs you upload.\nDepending on your Adform setup, supported identifier types can include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "First-party IDs"
        }), "\n", _jsx(_components.li, {
          children: "Mobile advertising IDs (MAIDs)"
        }), "\n", _jsx(_components.li, {
          children: "Adform cookies"
        }), "\n", _jsx(_components.li, {
          children: "ID5 identifiers"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After you select an identity type, map the column from your model that contains that identifier to ", _jsx(_components.strong, {
          children: "User ID"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally set a batch size for segment uploads.\nThe default is ", _jsx(_components.code, {
          children: "10000"
        }), ", and Adform limits each upload file to 100 MB."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "adform-dmp-token-request-failed-with-invalid_scope",
        children: "Adform DMP token request failed with invalid_scope"
      }), "\n", _jsx(_components.p, {
        children: "This error means the OAuth client configured on your Adform destination is missing one or more required DMP scopes."
      }), "\n", _jsx(_components.p, {
        children: "Ask Adform Support to grant these scopes to your OAuth client:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.segments"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.categories"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.authenticated"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "https://api.adform.com/scope/dmp.dataproviders.readonly"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "After Adform updates the client, test the destination connection again."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
